import { CommonModule } from '@angular/common'
import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, OnInit, ViewEncapsulation } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { RouterModule } from '@angular/router'
import { AnimationOptions } from 'ngx-lottie'
import { Subscription } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { CategoryService } from 'src/app/service/data/category.service'
import { DictionaryService } from 'src/app/service/data/dictionary.service'
import { TagService } from 'src/app/service/data/tag.service'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { Article, ContentTypes, Languages, PartOfSpeech, Themes } from '../../enums/enums'
import { SelectOption } from '../../interfaces/word'
import { Category } from '../../models/main/category'
import { SimpleDictionary } from '../../models/main/dictionary'
import { Tag } from '../../models/main/tag'
import { Word } from '../../models/main/word'
import { DdcCategorySComponent } from '../ddc-category-s/ddc-category-s.component'
import { DdcCountryFlagComponent } from '../ddc-country-flag/ddc-country-flag.component'
import { DdcDefinitionComponent } from '../ddc-definition/ddc-definition.component'
import { DdcDicsComponent } from '../ddc-dics/ddc-dics.component'
import { DdcExampleComponent } from '../ddc-example/ddc-example.component'
import { DdcPrivacyComponent } from '../ddc-privacy/ddc-privacy.component'
import { DdcTagSComponent } from '../ddc-tag-s/ddc-tag-s.component'
import { DdcTextGradientComponent } from '../ddc-text-gradient/ddc-text-gradient.component'
@Component({
  selector: 'ddc-word-dialog',
  templateUrl: './ddc-word-dialog.component.html',
  styleUrl: './ddc-word-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    AngularMaterialsModules,
    CommonModule,
    RouterModule,
    DdcTextGradientComponent,
    DdcPrivacyComponent,
    DdcCountryFlagComponent,
    DdcCategorySComponent,
    DdcTagSComponent,
    DdcDefinitionComponent,
    DdcExampleComponent,
    DdcDicsComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DdcWordDialogComponent implements OnInit {
  _isLightTheme: boolean = true
  _isDarkTheme: boolean = false
  _isPaperTheme: boolean = false
  _theme: string
  _addedField: string = ''
  private themeSubscription: Subscription

  _isCreateBtnLoading = false
  _submitBtnText = 'Edit'
  _privacyMaxlength: number = 4
  _wordMaxlength: number = 100
  _exampleMaxlength: number = 5000
  _definitionMaxlength: number = 10000
  _posMaxlength: number = 3
  _articleMaxlength: number = 3
  _addError: string = ''

  _selectedChipOptions: string[] = []
  _selectedPrivacyChipOptions: string = ''
  _userTags: any[] = []
  _userCats: any[] = []
  _userDics: any[] = []

  _loadedCats: boolean = false
  _loadedTags: boolean = false
  _loadedDics: boolean = false

  _selectedTags: Tag[] = []
  _selectedDics: SimpleDictionary[] = []
  _selectedArticle: string = null
  _selectedPos: string = null
  _selectedId: number = 0
  _selectedLanguage: string
  _selectedWord: string
  _selectedExample: string
  _selectedDefinition: string
  _selectedCategory: Category[] = []

  languages: SelectOption[] = []
  articles: SelectOption[] = []
  poses: SelectOption[] = []

  _isTouched: boolean = false

  _selectedLngHasArticle: boolean = false

  _isEditBtnLoading = false
  _maxLength: number = 100
  _editError: string = ''
  _editedField: string = ''

  _deletedField: boolean = false
  _deleteFromDDC: boolean = false
  _deleteFromDic: boolean = false

  _type: string = ''
  _element: Word
  _hasDic: boolean = false
  _targetDicId: number

  _isLiked: boolean = false
  _isBookmarked: boolean = false

  // This is the option that uses the package's AnimationOption interface
  options: AnimationOptions = {
    path: '/assets/lottie/green-tick.json',
    loop: false, // Set loop to false for a single playthrough
    autoplay: true,
  }

  _word_c_type = ContentTypes.WORD

  partOfSpeeches: any

  constructor(
    private logger: DdcLoggerService,
    public themeService: ThemeService,
    private cat_service: CategoryService,
    private tag_service: TagService,
    private dic_service: DictionaryService,
    public dialog: MatDialog,
    private editDialogRef: MatDialogRef<DdcWordDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    this._element = this.data.element

    if (this.data.hasDic) {
      this._hasDic = this.data.hasDic
      this._targetDicId = this.data.dicId
    }

    this._selectedPrivacyChipOptions = this._element.privacy
    this._selectedCategory[0] = this._element.category
    this._selectedTags = this._element.tags
    this._selectedDics = this._element.dictionaries
    this._selectedId = this._element.id
    this._selectedLanguage = this._element.language
    this._selectedArticle = this._element.article
    this._selectedWord = this._element.word
    this._selectedPos = this._element.pos
    this._selectedExample = this._element.example
    this._selectedDefinition = this._element.definition

    this.themeSubscription = this.themeService.theme$.subscribe(theme => {
      this._isLightTheme = theme === Themes.LIGHT
      this._isDarkTheme = theme === Themes.DARK
      this._isPaperTheme = theme === Themes.PAPER
    })

    this.getLanguages()
    this.getArticles()
    this.getPos()
    this.getUserTags()
    this.getUserCats()
    this.getUserDics()
  }

  getPos(): void {
    this.logger.info('DdcWordDialogComponent getPos()')

    const posArray = Object.values(PartOfSpeech) as string[]

    this.poses.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of posArray) {
      this.poses.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  getLanguages(): void {
    this.logger.info('DdcWordDialogComponent getLanguages()')

    type Language = keyof typeof Languages
    this.languages = Object.entries(Languages).map(([key, [viewValue, rawValue]]) => ({
      raw_value: rawValue,
      view_value: viewValue,
    }))
  }

  getArticles(): void {
    this.logger.info('DdcWordDialogComponent getArticles()')

    const articlesArray = Object.values(Article) as string[]

    this.articles.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of articlesArray) {
      this.articles.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  getUserTags(): void {
    this.logger.info('DdcWordDialogComponent getUserTags()')

    this.tag_service.get_tags().subscribe((res: any) => {
      if (res) {
        this._userTags = res
        this._loadedTags = true
      }
    })
  }

  getUserDics(): void {
    this.logger.info('DdcWordDialogComponent getUserDics()')

    this.dic_service.get_dics(true).subscribe((res: any) => {
      if (res) {
        this._userDics = res
        this._loadedDics = true
      }
    })
  }

  getUserCats(): void {
    this.logger.info('DdcWordDialogComponent getUserCats()')
    this.cat_service.get_cats().subscribe((res: any) => {
      if (res) {
        this._userCats = res
        this._loadedCats = true
      }
    })
  }

  handleTouched(): void {
    this._isTouched = true
  }

  closeDialog(): void {
    if (this._isTouched) this.editDialogRef.close('word')
    else this.editDialogRef.close('not-touched')
  }
}
